import type { ProfileResource } from "@medplum/core";
import type { DayOfWeek, SchedulingParameters } from "@repo/api";
import type { RecurringPatternInput as RecurringPattern } from "@repo/api/schemas";
import { scheduleBlockIdSchema } from "@repo/api/schemas";
import type { ScheduleBlockId, ScheduleId } from "@repo/domain";
import { mintEntityId } from "@repo/domain";

type BlockType = "one-time" | "recurring";

import {
  type AvailabilityBlockDto,
  adjustSchedule,
  blockAvailability,
  openSchedule,
  unblockAvailability,
  useSchedule,
  useSchedules,
} from "@repo/data-store";
import { useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";
import type { ActorSelection } from "@/features/scheduling/components/actor-selector";
import { useSchedulePermissions } from "@/features/scheduling/hooks/use-schedule-permissions";
import {
  ALL_DAYS,
  ALWAYS_AVAILABLE_END,
  ALWAYS_AVAILABLE_START,
  DEFAULT_SCHEDULE_SLOT_DURATION,
} from "@/features/scheduling/lib/constants";
import { useAppForm, withForm } from "@/shared/forms/app-form";
import type { DateRangeValue } from "@/shared/forms/fields";
import { practitionerDomainIdFromProfile } from "@/shared/hooks/use-current-practitioner";

// === Utilities ===

/** Form-shaped scheduling fields (drops per-HS `service` and other server-only fields). */
export type FormSchedulingShape = Omit<SchedulingParameters, "service">;

/**
 * The three fields this actually reads. Typed structurally rather than as
 * `SchedulingParameters` because callers pass the data-store DTO's own
 * scheduling params, which carry `serviceType` instead of the server-only
 * `service` — identical where it matters, so every call site was casting.
 */
type SchedulingParamsSource = Pick<FormSchedulingShape, "duration" | "timezone" | "availability">;

export function parseScheduleExtensions(params: SchedulingParamsSource | undefined): FormSchedulingShape | undefined {
  if (!params) return undefined;
  return {
    duration: params.duration,
    ...(params.timezone ? { timezone: params.timezone } : {}),
    ...(params.availability ? { availability: params.availability } : {}),
  };
}

// === Types ===

export interface BlockData {
  blockType: BlockType;
  start: string;
  end: string;
  reason?: string;
  recurring?: RecurringPattern;
}

export interface PendingBlockChange {
  type: "add" | "delete";
  block?: BlockData;
  blockId?: ScheduleBlockId | `pending-${number}`;
}

export interface NewBlockFormValues {
  type: BlockType;
  dateRange: DateRangeValue;
  startTime: string;
  endTime: string;
  frequency: "daily" | "weekly";
  dayOfWeek: DayOfWeek[];
  effectiveDateRange: DateRangeValue;
  allDay: boolean;
  reason: string;
}

export const DEFAULT_NEW_BLOCK: NewBlockFormValues = {
  type: "one-time",
  dateRange: { from: null, to: null },
  startTime: "",
  endTime: "",
  frequency: "daily",
  dayOfWeek: [],
  effectiveDateRange: { from: null, to: null },
  allDay: false,
  reason: "",
};

export interface ScheduleFormValues {
  active: boolean;
  alwaysAvailable: boolean;
  timezone: string;
  availabilityDays: DayOfWeek[];
  availabilityTime: { start: string; end: string };
  blockedTimeChanges: PendingBlockChange[];
  newBlock: NewBlockFormValues;
}

export interface BlockInfo {
  blockId: ScheduleBlockId | `pending-${number}`;
  blockType: BlockType;
  start: string;
  end: string;
  reason?: string;
  recurring?: RecurringPattern;
}

// === Constants ===

export const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

// === Helper Functions ===

export function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

type SchedulingFormFields = Pick<
  ScheduleFormValues,
  "alwaysAvailable" | "timezone" | "availabilityDays" | "availabilityTime"
>;

export function isAlwaysAvailable(params: FormSchedulingShape | undefined): boolean {
  if (!params?.availability) return false;
  const { dayOfWeek, startTime, endTime } = params.availability;
  return dayOfWeek.length === 7 && startTime === ALWAYS_AVAILABLE_START && endTime === ALWAYS_AVAILABLE_END;
}

export function parseSchedulingParams(params: FormSchedulingShape | undefined): SchedulingFormFields {
  const alwaysAvail = isAlwaysAvailable(params);
  return {
    alwaysAvailable: alwaysAvail,
    timezone: params?.timezone ?? getDefaultTimezone(),
    availabilityDays: params?.availability?.dayOfWeek ?? [],
    availabilityTime: {
      start: params?.availability?.startTime ?? "",
      end: params?.availability?.endTime ?? "",
    },
  };
}

export function buildSchedulingParams(values: ScheduleFormValues): FormSchedulingShape {
  if (values.alwaysAvailable) {
    return {
      duration: { ...DEFAULT_SCHEDULE_SLOT_DURATION },
      timezone: values.timezone,
      availability: { dayOfWeek: ALL_DAYS, startTime: ALWAYS_AVAILABLE_START, endTime: ALWAYS_AVAILABLE_END },
    };
  }
  return {
    duration: { ...DEFAULT_SCHEDULE_SLOT_DURATION },
    timezone: values.timezone,
    availability:
      values.availabilityDays.length > 0
        ? {
            dayOfWeek: values.availabilityDays,
            startTime: values.availabilityTime.start,
            endTime: values.availabilityTime.end,
          }
        : undefined,
  };
}

export function isBlockInPast(block: BlockInfo): boolean {
  const now = new Date();
  if (block.blockType === "one-time") {
    return new Date(block.end) < now;
  }
  if (block.blockType === "recurring" && block.recurring?.endDate) {
    return block.recurring.endDate < format(now, "yyyy-MM-dd");
  }
  return false;
}

export function parseSlotToBlockInfo(slot: AvailabilityBlockDto): BlockInfo {
  const recurring = slot.recurring;
  return {
    blockId: slot.blockId,
    blockType: recurring ? "recurring" : "one-time",
    start: slot.start,
    end: slot.end,
    reason: slot.reason,
    recurring,
  };
}

export function formatTimeRange(start: string, end: string): string {
  if (start.length === 5 && end.length === 5) {
    if (isAllDayBlock(start, end)) return "All Day";
    const formatTime = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      const period = h >= 12 ? "PM" : "AM";
      const hour = h % 12 || 12;
      return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  }

  try {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const startTime = format(startDate, "HH:mm");
    const endTime = format(endDate, "HH:mm");
    const sameDay = format(startDate, "yyyy-MM-dd") === format(endDate, "yyyy-MM-dd");

    if (isAllDayBlock(startTime, endTime)) {
      if (sameDay) return format(startDate, "MMM d, yyyy");
      return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
    }

    if (sameDay) {
      return `${format(startDate, "MMM d, yyyy")} ${format(startDate, "h:mm a")} - ${format(endDate, "h:mm a")}`;
    }
    return `${format(startDate, "MMM d, yyyy h:mm a")} - ${format(endDate, "MMM d, yyyy h:mm a")}`;
  } catch {
    return `${start} - ${end}`;
  }
}

export function formatDaysOfWeek(days: DayOfWeek[]): string {
  const dayMap: Record<DayOfWeek, string> = {
    sun: "Sun",
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
  };
  return days.map((d) => dayMap[d]).join(", ");
}

export function combineDateTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(":").map(Number);
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
}

export async function processBlockChanges(scheduleId: ScheduleId, changes: PendingBlockChange[]) {
  for (const change of changes) {
    if (change.type === "add" && change.block) {
      await blockAvailability({
        scheduleId,
        blockType: change.block.blockType,
        start: change.block.start,
        end: change.block.end,
        reason: change.block.reason,
        recurring: change.block.recurring,
      });
    } else if (change.type === "delete" && change.blockId && !change.blockId.startsWith("pending-")) {
      await unblockAvailability({ scheduleId, blockId: scheduleBlockIdSchema.parse(change.blockId) }).isPersisted
        .promise;
    }
  }
}

export function isAllDayBlock(startTime: string, endTime: string): boolean {
  return startTime === "00:00" && (endTime === "23:59" || endTime === "00:00");
}

export function blockInfoToFormValues(block: BlockInfo): NewBlockFormValues {
  if (block.blockType === "one-time") {
    const startDate = parseISO(block.start);
    const endDate = parseISO(block.end);
    const startTime = format(startDate, "HH:mm");
    const endTime = format(endDate, "HH:mm");
    return {
      type: "one-time",
      dateRange: { from: startDate, to: endDate },
      startTime,
      endTime,
      frequency: "daily",
      dayOfWeek: [],
      effectiveDateRange: { from: null, to: null },
      allDay: isAllDayBlock(startTime, endTime),
      reason: block.reason ?? "",
    };
  }
  return {
    type: "recurring",
    dateRange: { from: null, to: null },
    startTime: block.start,
    endTime: block.end,
    frequency: block.recurring?.frequency ?? "daily",
    dayOfWeek: block.recurring?.dayOfWeek ?? [],
    effectiveDateRange: {
      from: block.recurring?.startDate ? parseISO(block.recurring.startDate) : null,
      to: block.recurring?.endDate ? parseISO(block.recurring.endDate) : null,
    },
    allDay: isAllDayBlock(block.start, block.end),
    reason: block.reason ?? "",
  };
}

// === Schema ===

const dayOfWeekEnum = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

const dateRangeSchema = z.object({ from: z.date().nullable(), to: z.date().nullable() });

const recurringPatternSchema = z.object({
  frequency: z.enum(["daily", "weekly"]),
  dayOfWeek: z.array(dayOfWeekEnum).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

const blockDataSchema = z.object({
  blockType: z.enum(["one-time", "recurring"]),
  start: z.string(),
  end: z.string(),
  reason: z.string().optional(),
  recurring: recurringPatternSchema.optional(),
});

const pendingBlockChangeSchema = z.object({
  type: z.enum(["add", "delete"]),
  block: blockDataSchema.optional(),
  id: z.string().optional(),
});

const newBlockSchema = z.object({
  type: z.enum(["one-time", "recurring"]),
  dateRange: dateRangeSchema,
  startTime: z.string(),
  endTime: z.string(),
  frequency: z.enum(["daily", "weekly"]),
  dayOfWeek: z.array(dayOfWeekEnum),
  effectiveDateRange: dateRangeSchema,
  allDay: z.boolean(),
  reason: z.string(),
});

export const scheduleFormSchema = z
  .object({
    active: z.boolean(),
    alwaysAvailable: z.boolean(),
    timezone: z.string(),
    availabilityDays: z.array(dayOfWeekEnum),
    availabilityTime: z.object({ start: z.string(), end: z.string() }),
    blockedTimeChanges: z.array(pendingBlockChangeSchema),
    newBlock: newBlockSchema,
  })
  .superRefine((data, ctx) => {
    if (data.alwaysAvailable) return;
    if (data.availabilityDays.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Select at least one day", path: ["availabilityDays"] });
    }
    if (!data.availabilityTime.start) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Start time required", path: ["availabilityTime"] });
    }
    if (!data.availabilityTime.end) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "End time required", path: ["availabilityTime"] });
    }
    if (
      data.availabilityTime.start &&
      data.availabilityTime.end &&
      data.availabilityTime.start >= data.availabilityTime.end
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start time must be before end time",
        path: ["availabilityTime"],
      });
    }
  });

// === Hook ===

interface UseScheduleFormOptions {
  scheduleId?: string;
  profile: ProfileResource | undefined;
  mode: "create" | "edit";
  defaultActor?: ActorSelection;
  onSuccess?: () => void;
}

interface UseScheduleFormReturn {
  defaultValues: ScheduleFormValues;
  validators: { onDynamic: typeof scheduleFormSchema };
  handleSubmit: (value: ScheduleFormValues) => Promise<void>;
  schedule: ReturnType<typeof useSchedule>["schedule"];
  isLoading: boolean;
  isReady: boolean;
  isPending: boolean;
  isNew: boolean;
  canWrite: boolean;
  existingParams: FormSchedulingShape | undefined;
  selectedActor: ActorSelection | undefined;
  setSelectedActor: React.Dispatch<React.SetStateAction<ActorSelection | undefined>>;
}

export function useScheduleFormData({
  scheduleId,
  profile,
  mode,
  defaultActor,
  onSuccess,
}: UseScheduleFormOptions): UseScheduleFormReturn {
  const navigate = useNavigate();

  const practitionerDomainId = practitionerDomainIdFromProfile(profile);
  const myActorRef = practitionerDomainId ? ["Practitioner", practitionerDomainId].join("/") : undefined;
  const byIdResult = useSchedule(scheduleId);
  const myResult = useSchedules(myActorRef);
  const schedule = mode === "create" ? null : scheduleId ? byIdResult.schedule : (myResult.schedules[0] ?? null);
  const isLoading = scheduleId ? byIdResult.isLoading : myResult.isLoading;
  const isReady = scheduleId ? byIdResult.isReady : myResult.isReady;

  const [isPending, setIsPending] = React.useState(false);
  const isNew = !schedule;
  const { canWrite } = useSchedulePermissions(schedule, profile);

  const [selectedActor, setSelectedActor] = React.useState<ActorSelection | undefined>(defaultActor);

  const existingParams = React.useMemo(() => {
    const base = schedule?.schedulingParameters.find((p) => !p.serviceType) ?? schedule?.schedulingParameters[0];
    return parseScheduleExtensions(base);
  }, [schedule?.schedulingParameters]);

  const parsedParams = parseSchedulingParams(existingParams);

  const defaultValues: ScheduleFormValues = {
    active: schedule?.active ?? true,
    blockedTimeChanges: [],
    newBlock: { ...DEFAULT_NEW_BLOCK },
    ...parsedParams,
  };

  const handleSubmit = async (value: ScheduleFormValues) => {
    setIsPending(true);
    try {
      const built = buildSchedulingParams(value);
      const availability = built.availability;
      const timezone = built.timezone;

      if (isNew && selectedActor && selectedActor.type !== "Other") {
        // Collect add-only block changes as initialBlocks for atomic creation
        const initialBlocks = value.blockedTimeChanges
          .flatMap((change) => (change.type === "add" && change.block ? [change.block] : []))
          .map((b) => ({
            blockType: b.blockType as "one-time" | "recurring",
            start: b.start,
            end: b.end,
            ...(b.reason ? { reason: b.reason } : {}),
            ...(b.recurring ? { recurring: b.recurring } : {}),
          }));
        // Top-level availability+timezone triggers auto-fan-out across active protocol HSs.
        await openSchedule({
          id: mintEntityId(),
          active: value.active,
          actorRef: [selectedActor.type, selectedActor.id].join("/"),
          actorDisplay: selectedActor.display,
          ...(availability ? { availability } : {}),
          ...(timezone ? { timezone } : {}),
          ...(initialBlocks.length > 0 && { initialBlocks }),
        });
      } else if (schedule?.scheduleId) {
        await adjustSchedule({
          id: schedule.scheduleId,
          active: value.active,
          availability,
          timezone,
        });

        if (value.blockedTimeChanges.length > 0) {
          await processBlockChanges(schedule.scheduleId, value.blockedTimeChanges);
        }
      }

      toast.success(isNew ? "Schedule created" : "Schedule updated");
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(scheduleId ? { to: "/schedule/manage" } : { to: "/schedule", search: { view: "week" } });
      }
    } catch {
      // The shared outbox reports save failures.
    } finally {
      setIsPending(false);
    }
  };

  return {
    defaultValues,
    validators: { onDynamic: scheduleFormSchema },
    handleSubmit,
    schedule,
    isLoading,
    isReady,
    isPending,
    isNew,
    canWrite,
    existingParams,
    selectedActor,
    setSelectedActor,
  };
}

export { useAppForm, withForm };
